import React, {FC} from "react";
import {BlockMarkupViewerProps} from "./blockMarkupViewer.interface";
import {BlockMarkupItem, BlockMarkupItemType, CodeMarkup, ImageMarkup, ListMarkup, PlainTextMarkup, exhaustiveFail} from "../../common";
import {ImageViewer} from "./imageViewer/imageViewer";
import styled from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { renderInlineMarkup } from "../renderInlineMarkup";

const Root = styled.div`
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    width: 100%;
`;

const TextSpan = styled.span<ThemeProps>`
    text-align: center;
    display: block;
    color: ${props => props.theme.primaryForeground};
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
`;

const CodePre = styled.pre<ThemeProps>`
    font-size: 0.7em;
    color: ${props => props.theme.primaryForeground};
    overflow-x: auto;
    max-width: 100%;
`;

const ImageContainer = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
`;

const StyledList = styled.ul`
    text-align: left;
    margin: 4px 0;
    padding-left: 1.4em;
`;

const StyledOrderedList = styled.ol`
    text-align: left;
    margin: 4px 0;
    padding-left: 1.4em;
`;


function renderItem(item: BlockMarkupItem, index: number, cardId: string | undefined) {
    const type = item.type;
    let plainTextItem: PlainTextMarkup;
    let imageItem: ImageMarkup;
    let codeItem: CodeMarkup;
    let listMarkupItem: ListMarkup;

    switch (type) {
        case BlockMarkupItemType.PlainText:
            plainTextItem = item as PlainTextMarkup;
            return <TextSpan key={index}>{renderInlineMarkup(plainTextItem.text)}</TextSpan>;
        case BlockMarkupItemType.Image:
            imageItem = item as ImageMarkup;
            return (
                <ImageContainer key={index}>
                    {React.createElement(ImageViewer, { cardId, imageId: imageItem.imageId })}
                </ImageContainer>
            );
        case BlockMarkupItemType.Code:
            codeItem = item as CodeMarkup;
            return <CodePre key={index}><code>{codeItem.code}</code></CodePre>;
        case BlockMarkupItemType.List:
            listMarkupItem = item as ListMarkup;
            if (listMarkupItem.ordered) {
                return (
                    <StyledOrderedList key={index}>
                        {listMarkupItem.items.map((text, i) => <li key={i}>{renderInlineMarkup(text)}</li>)}
                    </StyledOrderedList>
                );
            }
            return (
                <StyledList key={index}>
                    {listMarkupItem.items.map((text, i) => <li key={i}>{renderInlineMarkup(text)}</li>)}
                </StyledList>
            );
        default:
            exhaustiveFail(type);
    }
}

const BlockMarkupViewerComponent: FC<BlockMarkupViewerProps> = props => {
    if (props.blockMarkup.length === 0)
        return null;

    return (
        <Root>
            {props.blockMarkup.map((item, index) => renderItem(item, index, props.cardId))}
        </Root>
    );
};

BlockMarkupViewerComponent.displayName = 'BlockMarkupViewer';

export const BlockMarkupViewer = BlockMarkupViewerComponent;
